import React, { useEffect, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { useField } from '@unform/core';

type TVTextFieldProps = TextFieldProps & {
  name: string;
  calcular?: (novoTextoselect: string | undefined) => void;
  percentage?: boolean; // Nova prop para indicar se é porcentagem
};

export const VFieldMoneypercentual: React.FC<TVTextFieldProps> = ({ 
  calcular, 
  name, 
  percentage = false, // Valor padrão: false
  ...rest 
}) => {
  const { fieldName, registerField, defaultValue, error, clearError } = useField(name);
  const [value, setValue] = useState<string>(defaultValue || '');

  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => formatValueForStorage(value),
      setValue: (_, newValue) => {
        const formattedValue = formatValueForDisplay(newValue?.toString() || '');
        setValue(formattedValue);
      },
    });
  }, [registerField, fieldName, value]);

  // Formata o valor para armazenamento (número puro)
  const formatValueForStorage = (valueStr: string): string => {
    if (!valueStr) return '';
    
    if (percentage) {
      // Para porcentagem, remove tudo exceto números e ponto decimal
      valueStr = valueStr.replace(/[^\d,]/g, '');
      valueStr = valueStr.replace(',', '.');
      return valueStr;
    } else {
      // Para dinheiro, formata como antes
      valueStr = valueStr.replace(/\D/g, '');
      valueStr = valueStr.replace(/(\d)(\d{2})$/, '$1.$2');
      valueStr = valueStr.replace(/(?=(\d{3})+(\D))\B/g, '');
      return valueStr;
    }
  };

  // Formata o valor para exibição
  const formatValueForDisplay = (valueStr: string): string => {
    if (!valueStr) return '';
    
    if (percentage) {
      // Para porcentagem: remove não-numéricos, converte vírgula para ponto
      let numericValue = valueStr.replace(/[^\d,.]/g, '');
      numericValue = numericValue.replace(',', '.');
      
      // Se não tiver símbolo de porcentagem, adiciona
      if (!valueStr.includes('%')) {
        return numericValue;
      }
      return numericValue;
    } else {
      // Para dinheiro: formatação monetária padrão
      valueStr = valueStr.replace(/\D/g, '');
      if (valueStr === '') return '';
      
      // Adiciona centavos se necessário
      if (valueStr.length === 1) {
        valueStr = `0,0${valueStr}`;
      } else if (valueStr.length === 2) {
        valueStr = `0,${valueStr}`;
      } else {
        valueStr = valueStr.replace(/(\d{2})$/, ',$1');
      }
      
      // Adiciona separadores de milhar
      const parts = valueStr.split(',');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return parts.join(',');
    }
  };

  // Formata o valor enquanto digita
  const formatInputValue = (inputValue: string): string => {
    if (!inputValue) return '';
    
    if (percentage) {
      // Para porcentagem: permite apenas números, vírgula e ponto
      inputValue = inputValue.replace(/[^\d,.]/g, '');
      
      // Remove pontos extras (só permite um ponto/vírgula decimal)
      const decimalSeparator = inputValue.includes(',') ? ',' : '.';
      inputValue = inputValue.replace(/[.,]/g, (match) => {
        return match === decimalSeparator ? match : '';
      });
      
      // Se houver mais de uma vírgula/ponto, mantém apenas o primeiro
      const separatorIndex = inputValue.search(/[.,]/);
      if (separatorIndex !== -1) {
        const beforeSeparator = inputValue.substring(0, separatorIndex + 1);
        const afterSeparator = inputValue.substring(separatorIndex + 1).replace(/[.,]/g, '');
        inputValue = beforeSeparator + afterSeparator;
      }
      
      // Limita a 2 casas decimais
      if (separatorIndex !== -1) {
        const decimalPart = inputValue.substring(separatorIndex + 1);
        if (decimalPart.length > 2) {
          inputValue = inputValue.substring(0, separatorIndex + 3);
        }
      }
      
      return inputValue;
    } else {
      // Para dinheiro: formatação monetária padrão
      inputValue = inputValue.replace(/\D/g, '');
      
      if (inputValue === '') return '';
      
      // Adiciona centavos
      if (inputValue.length === 1) {
        inputValue = `0,0${inputValue}`;
      } else if (inputValue.length === 2) {
        inputValue = `0,${inputValue}`;
      } else {
        inputValue = inputValue.replace(/(\d{2})$/, ',$1');
      }
      
      // Adiciona separadores de milhar
      const parts = inputValue.split(',');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return parts.join(',');
    }
  };

  return (
    <TextField  
      {...rest}
      error={!!error}
      helperText={error}
      value={value}
      onChange={(e) => {
        const formattedValue = formatInputValue(e.target.value);
        setValue(formattedValue);
        
        // Chama a função calcular com o valor formatado para armazenamento
        const storageValue = formatValueForStorage(formattedValue);
        calcular?.(storageValue);
        
        // Chama o onChange original se existir
        const eventWithFormattedValue = {
          ...e,
          target: {
            ...e.target,
            value: formattedValue,
          },
        };
        rest.onChange?.(eventWithFormattedValue);
      }}
      onKeyDown={(e) => {
        if (error) { 
          clearError(); 
        }
        rest.onKeyDown?.(e);
      }}
      // Adiciona sufixo de % se for porcentagem
      InputProps={{
        ...rest.InputProps,
        endAdornment: percentage ? (
          <span style={{ marginLeft: 4, color: '#666' }}>%</span>
        ) : undefined,
      }}
    />
  );
};